import "./publicPath"; // Must be first — sets __webpack_public_path__ for embedded mode
import React from "react";
import ReactDOM from "react-dom";
import "./index.scss";
import "./styles/latex_fonts.scss";
import App from "./App";
import { StylesProvider } from "@material-ui/core/styles";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Provider } from "react-redux";
import { store } from "./Redux/store";
import { sandboxGenerateClassName } from "./mfe/jssClassNameGenerator";
import { initSentry } from "./sentry";
import { setupSandboxPlugin } from "./mfe/sandboxPlugin";
import { toast } from "react-toastify";
import { bootstrapSessionFromCookie } from "./Core/utils/bootstrapSessionFromCookie";
import { registerAuthSyncListener } from "./Core/utils/authSyncListener";
import {
  SESSION_EXPIRED_FLAG,
  SESSION_EXPIRED_MESSAGE,
} from "./Core/sessionExpired";

import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";

initSentry();

// React version-safe render helper (supports React 16/17 and React 18)
function renderApp(container, component) {
  if (ReactDOM.createRoot) {
    const root = ReactDOM.createRoot(container);
    root.render(component);
    return root;
  }
  ReactDOM.render(component, container);
}

// -------------------------------------------------------------------------
// EMBEDDED MODE — running inside StudentPortal shell.
// All MFE logic lives in ./mfe/sandboxPlugin.js.
// To remove the MFE architecture: delete that file and this block.
// -------------------------------------------------------------------------
if (window.__SANDBOX_IS_PLUGIN__) {
  setupSandboxPlugin(store);
} else {
  // Standalone boot: if the user was just handed off from the portal-embedded
  // login (cy-sso cookies present, no sandbox_user yet), rebuild the session
  // before render so the route guard sees it on first paint. Also re-adopts the
  // cookie token when it changed (source of truth, e.g. a portal profile switch).
  bootstrapSessionFromCookie();
  // Keep an already-open standalone tab in sync when the shared cookies change
  // in another tab/app (re-reads on focus / visibility).
  registerAuthSyncListener();

  // One-shot "session expired" toast, set by Core/sessionExpired.js before its
  // redirect to the login page. Fired here so it survives the reload.
  try {
    if (sessionStorage.getItem(SESSION_EXPIRED_FLAG)) {
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
      toast.configure();
      setTimeout(() => toast.error(SESSION_EXPIRED_MESSAGE), 0);
    }
  } catch (_) {
    /* ignore */
  }

  if (window.ReactNativeWebView) {
    document.addEventListener("DOMContentLoaded", () => {
      document.body.classList.add("root-no-scroll");
      document.getElementById("root")?.classList.add("root-no-scroll");
    });
  }

  const container = document.getElementById("root");
  if (container) {
    container.classList.add("sandbox-revamp");
    renderApp(
      container,
      <React.StrictMode>
        <StylesProvider generateClassName={sandboxGenerateClassName}>
          <Provider store={store}>
            <GoogleOAuthProvider
              clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}
            >
              <App />
            </GoogleOAuthProvider>
          </Provider>
        </StylesProvider>
      </React.StrictMode>,
    );
  }
}
